import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { TableComponent } from '@custom-components/general/table/table.component';
import {
  FilterGroup,
  TableFilterBarComponent,
} from '@custom-components/general/table-filter-bar/table-filter-bar.component';
import {
  ChartComponent,
  XemexChartDataset,
} from '@custom-components/general/chart/chart.component';
import { CardComponent } from '@custom-components/general/card';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import {
  TagStateOf,
  alarmConfigGroupLabelKey,
  configGroupForAlarmType,
  displayAlarmTypeName,
} from '@core/helpers';
import { APP_PATHS } from '@core/constants';
import { AlarmOverviewKpisModel, AlarmOverviewRowModel, capitalizePriority } from '@core/models';
import { AlarmsOverviewService } from '@core/services/alarms-overview.service';
import { AlarmTypeCatalogService } from '@core/services/alarm-type-catalog.service';
import { StatCardComponent } from '@custom-components/general/stat-card/stat-card.component';

/** Internal display/filter row shape — decoupled from the API's field names/casing so the
 * existing client-side filter/age-bucket/chart logic doesn't need to know about the API. */
interface AlarmOverviewRow {
  id: string;
  deviceId: string;
  deviceLdn: string;
  type: string;
  priority: 'High' | 'Medium' | 'Low';
  location: string;
  /** Raw ISO timestamp from the API, kept verbatim so the displayed "Opened at" column
   * doesn't drift while the tab stays open (see openedMinutesAgo below, used for filtering only). */
  openedAt: string;
  openedMinutesAgo: number;
  /** Always 'Open' today — see AlarmOverviewRowModel.state. */
  state: 'Open' | 'Closed';
}

interface AlarmOverviewDisplayRow extends Record<string, unknown> {
  id: string;
  deviceId: string;
  deviceLdn: string;
  type: string;
  priority: string;
  priorityState: string;
  location: string;
  openedAt: string;
  state: string;
  age: string;
}

const MINUTE_MS = 60_000;
const DAY_MINUTES = 1440;
const OPEN_DATE_PRESETS = ['all', 'last24h', 'last7d', 'last30d'] as const;
const AGE_BUCKETS = ['< 1 hour', '1 hr – 24 hrs', '1 day – 7 days', '> 7 days'];

@Component({
  selector: 'xemex-alarms-overview-tab',
  imports: [
    MatIconModule,
    TableComponent,
    TableFilterBarComponent,
    ChartComponent,
    StatCardComponent,
    CardComponent,
    TranslateModule,
  ],
  templateUrl: './alarms-overview-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [TranslatePipe, DatePipe],
})
export class AlarmsOverviewTabComponent implements OnInit {
  private readonly translateService = inject(TranslatePipe);
  private readonly datePipe = inject(DatePipe);
  private readonly alarmsOverviewService = inject(AlarmsOverviewService);
  private readonly alarmTypeCatalog = inject(AlarmTypeCatalogService);
  private readonly destroyRef = inject(DestroyRef);

  /** Pre-selects filters on load (e.g. deep-linked from the dashboard's alarm widgets via
   * `?priority=High`/`?type=...`) — passed straight through to the filter bar, and also seeds
   * `activeFilters` here directly since the bar only reports changes, not its initial state. */
  initialFilters = input<Record<string, string[]>>({});

  protected readonly APP_PATHS = APP_PATHS;
  protected readonly loading = signal(false);
  protected readonly loadError = signal(false);

  private readonly rows = signal<AlarmOverviewRow[]>([]);
  /** Server-computed KPIs/trend over the full open-alarm set (see AlarmsOverviewService) —
   * used directly instead of re-deriving them client-side from `rows`. */
  private readonly apiKpis = signal<AlarmOverviewKpisModel | null>(null);

  ngOnInit(): void {
    const init = this.initialFilters();
    if (Object.keys(init).length) this.activeFilters.set(init);

    this.loading.set(true);
    this.alarmsOverviewService
      .getOverview()
      .pipe(
        catchError(() => {
          this.loadError.set(true);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        this.loading.set(false);
        if (!result) return;
        this.rows.set(result.items.map(toDisplayInputRow));
        this.apiKpis.set(result.kpis);
      });
  }

  readonly columns: TableColumnConfig[] = [
    {
      field: 'deviceLdn',
      label: 'alarmsPage.overview.table.device',
      type: TableColumnType.Text,
      bold: true,
      width: '13%',
    },
    {
      field: 'type',
      label: 'alarmsPage.overview.table.type',
      type: TableColumnType.Text,
      width: '17%',
    },
    {
      field: 'priority',
      label: 'alarmsPage.overview.table.priority',
      type: TableColumnType.Tag,
      stateField: 'priorityState',
      width: '10%',
    },
    {
      field: 'location',
      label: 'alarmsPage.overview.table.location',
      type: TableColumnType.Text,
      width: '22%',
    },
    {
      field: 'openedAt',
      label: 'alarmsPage.overview.table.openedAt',
      type: TableColumnType.Text,
      width: '16%',
    },
    {
      field: 'state',
      label: 'alarmsPage.overview.table.state',
      type: TableColumnType.Text,
      width: '10%',
    },
    {
      field: 'age',
      label: 'alarmsPage.overview.table.age',
      type: TableColumnType.Text,
      width: '10%',
    },
  ];

  readonly query = signal('');
  readonly activeFilters = signal<Record<string, string[]>>({});

  readonly filterGroups = computed<FilterGroup[]>(() => {
    const types = [...new Set(this.rows().map(r => this.typeLabel(r.type)))];
    return [
      {
        key: 'type',
        label: this.translateService.transform('alarmsPage.overview.filters.type'),
        options: types,
      },
      {
        key: 'priority',
        label: this.translateService.transform('alarmsPage.overview.filters.priority'),
        options: ['High', 'Medium', 'Low'],
      },
      {
        key: 'state',
        label: this.translateService.transform('alarmsPage.overview.filters.state'),
        options: ['Open', 'Closed'],
      },
      {
        key: 'age',
        label: this.translateService.transform('alarmsPage.overview.filters.age'),
        options: AGE_BUCKETS,
      },
      {
        key: 'openedAt',
        label: this.translateService.transform('alarmsPage.overview.filters.openedAt'),
        selection: 'single',
        options: OPEN_DATE_PRESETS.map(preset =>
          this.translateService.transform(`alarmsPage.overview.filters.openedAtPresets.${preset}`),
        ),
      },
    ];
  });

  private matchesAge(row: AlarmOverviewRow, buckets: string[]): boolean {
    if (!buckets.length) return true;
    const mins = row.openedMinutesAgo;
    const map: Record<string, boolean> = {
      [AGE_BUCKETS[0]]: mins < 60,
      [AGE_BUCKETS[1]]: mins >= 60 && mins < DAY_MINUTES,
      [AGE_BUCKETS[2]]: mins >= DAY_MINUTES && mins < 7 * DAY_MINUTES,
      [AGE_BUCKETS[3]]: mins >= 7 * DAY_MINUTES,
    };
    return buckets.some(b => map[b]);
  }

  private matchesOpenedAt(row: AlarmOverviewRow, presetLabels: string[]): boolean {
    if (!presetLabels.length) return true;
    const preset = OPEN_DATE_PRESETS.find(
      p =>
        this.translateService.transform(`alarmsPage.overview.filters.openedAtPresets.${p}`) ===
        presetLabels[0],
    );
    if (!preset || preset === 'all') return true;
    const thresholds: Record<string, number> = {
      last24h: DAY_MINUTES,
      last7d: 7 * DAY_MINUTES,
      last30d: 30 * DAY_MINUTES,
    };
    return row.openedMinutesAgo <= thresholds[preset];
  }

  private readonly filteredRows = computed<AlarmOverviewRow[]>(() => {
    const q = this.query().toLowerCase();
    const filters = this.activeFilters();
    const typeFilters = filters['type'] ?? [];
    const priorityFilters = filters['priority'] ?? [];
    const stateFilters = filters['state'] ?? [];
    const ageFilters = filters['age'] ?? [];
    const openedAtFilters = filters['openedAt'] ?? [];

    return this.rows().filter(row => {
      if (typeFilters.length && !typeFilters.includes(this.typeLabel(row.type))) return false;
      if (priorityFilters.length && !priorityFilters.includes(row.priority)) return false;
      if (stateFilters.length && !stateFilters.includes(row.state)) return false;
      if (!this.matchesAge(row, ageFilters)) return false;
      if (!this.matchesOpenedAt(row, openedAtFilters)) return false;
      if (q && !row.deviceLdn.toLowerCase().includes(q) && !row.location.toLowerCase().includes(q))
        return false;
      return true;
    });
  });

  readonly tableData = computed<AlarmOverviewDisplayRow[]>(() =>
    [...this.filteredRows()]
      .sort((a, b) => a.openedMinutesAgo - b.openedMinutesAgo)
      .map(row => ({
        id: row.id,
        deviceId: row.deviceId,
        deviceLdn: row.deviceLdn,
        type: this.displayTypeName(row.type),
        priority: row.priority,
        priorityState: TagStateOf.alarmPriority(row.priority),
        location: row.location,
        openedAt: this.formatOpenedAt(row.openedAt),
        state: row.state,
        age: this.formatAge(row.openedMinutesAgo),
      })),
  );

  readonly kpis = computed(() => {
    const apiKpis = this.apiKpis();

    const openNow = apiKpis?.total_open ?? 0;
    const trend = apiKpis?.trend ?? [];
    // trend covers the last TREND_DAYS (server-side), oldest first — index length-8 is "7 days ago".
    // Fewer than 8 points (new deployment, sparse history) means there's no week-old data point
    // to diff against, so treat the delta as 0 rather than comparing against index -1/-2/etc.
    const weekAgoIndex = trend.length - 8;
    const existedWeekAgo = weekAgoIndex >= 0 ? (trend[weekAgoIndex]?.open_count ?? 0) : openNow;
    const openDelta = openNow - existedWeekAgo;
    const openedLast7 = apiKpis?.opened_last_7_days ?? 0;
    const avgPerDay = (openedLast7 / 7).toFixed(1);

    return {
      openNow,
      openDeltaStr: openDelta >= 0 ? `+${openDelta}` : `${openDelta}`,
      openedLast7,
      avgPerDay,
      // The overview API only ever returns currently-open alarms (see AlarmOverviewRowModel.state),
      // so there's no closed-alarm data to average yet — show 0 until the backend exposes a
      // real resolution-time figure.
      avgResStr: '0',
    };
  });

  readonly chartDatasets = computed<XemexChartDataset[]>(() => {
    const trend = this.apiKpis()?.trend ?? [];
    return [
      {
        label: this.translateService.transform('alarmsPage.overview.chart.title'),
        data: trend.map(point => point.open_count),
        type: 'line',
        color: '#16A085',
      },
    ];
  });

  readonly chartLabels = computed<string[]>(() =>
    (this.apiKpis()?.trend ?? []).map(point => this.datePipe.transform(point.date, 'd MMM') ?? ''),
  );

  /** Same grouped, translated label the settings tab shows for its alarm-type rows — derived
   * from the config_group an alarm type belongs to, not the raw per-event catalog key (which
   * for cloud types like `device_connection`/`FRAUD_EVENT` is literally the key itself).
   * Alarm types outside the alarm-config catalog (e.g. `modbus_error`, heatpump alarms) have no
   * config_group at all — `configGroupForAlarmType` falls back to returning the key unchanged,
   * so the groupLabels lookup below would just render that raw key. Fall back to the existing
   * `status.names.*` dictionary (already used for these device-alarm names elsewhere), and only
   * show the raw key as a last resort. */
  private typeLabel(type: string): string {
    const groupLabelKey = alarmConfigGroupLabelKey(configGroupForAlarmType(type));
    const groupLabel = this.translateService.transform(groupLabelKey);
    if (groupLabel !== groupLabelKey) return groupLabel;

    const statusNameKey = `status.names.${type}`;
    const statusName = this.translateService.transform(statusNameKey);
    return statusName !== statusNameKey ? statusName : type.replace(/_/g, ' ');
  }

  /** Renders the human label for a raw catalog key (e.g. "fraud.44" -> "Fraud Detection Event:
   * Meter cover removed") for the table's own `type` column, as opposed to `typeLabel` above
   * which groups every code under one category filter option. Shared with
   * DeviceAlarmsTabComponent via displayAlarmTypeName — same key shapes, same catalog. */
  private displayTypeName(type: string): string {
    return displayAlarmTypeName(
      type,
      k => this.alarmTypeCatalog.label(k),
      k => this.translateService.transform(k),
    );
  }

  private formatOpenedAt(openedAt: string): string {
    return this.datePipe.transform(openedAt, 'd MMM HH:mm') ?? '';
  }

  private formatAge(minutesAgo: number): string {
    if (minutesAgo < 60) return `${minutesAgo} min`;
    const hours = Math.floor(minutesAgo / 60);
    if (hours < 24) return `${hours} hrs`;
    return `${Math.floor(hours / 24)} days`;
  }

  protected readonly rowLink = (row: AlarmOverviewDisplayRow) => [
    '/',
    APP_PATHS.Devices,
    row.deviceId,
    'alarms',
  ];
}

function toDisplayInputRow(row: AlarmOverviewRowModel): AlarmOverviewRow {
  const openedAt = new Date(row.opened_at).getTime();
  const openedMinutesAgo = Math.max(0, Math.round((Date.now() - openedAt) / MINUTE_MS));
  return {
    id: row.id,
    deviceId: row.device_id,
    deviceLdn: row.device_ldn,
    type: row.alarm_type,
    priority: capitalizePriority(row.priority),
    location: row.location,
    openedAt: row.opened_at,
    openedMinutesAgo,
    state: row.state as 'Open' | 'Closed',
  };
}
